/**
 * Gate Pass In: the security desk's record of a loaded vehicle nothing announced.
 *
 * S-Capture trucks are passed in from the feed (`gate.ts`). Everything else that comes through the
 * barrier with a load — chemicals, packing material, machinery, QA samples — is typed by the guard
 * here, modelled on VNR's GatePass "Gate In" screen. The pass itself books nothing; the arrival
 * executive may earmark a submitted one for an arrival form (`GATE_PASS_IN_ARRIVAL_FORMS`).
 *
 * Its number comes from the same plant gate-in series as an S-Capture pass, so every vehicle let in
 * at a plant carries one running number.
 *
 *     partial ──submit──▶ submitted
 *        └──────cancel──────┴──▶ cancelled
 */
import { MOBILE_MESSAGE, MOBILE_PATTERN, normalizeMobile } from "./mobile";

export const GATE_PASS_IN_CLASSIFICATIONS = [
  "seeds",
  "packing_material",
  "chemicals",
  "machinery_or_parts",
  "qa_sample",
  "others",
  "remnant",
] as const;
export type GatePassInClassification =
  (typeof GATE_PASS_IN_CLASSIFICATIONS)[number];
export const GATE_PASS_IN_CLASSIFICATION_LABELS: Record<
  GatePassInClassification,
  string
> = {
  seeds: "Seeds",
  packing_material: "Packing Material",
  chemicals: "Chemicals",
  machinery_or_parts: "Machinery or Parts",
  qa_sample: "QA Sample",
  others: "Others",
  remnant: "Remnant",
};

export const GATE_PASS_IN_SECTIONS = [
  "arrival",
  "processing",
  "packing",
  "dispatch",
  "stores",
  "sales_return",
  "inventory",
  "qa_lab",
  "foundation_seed",
  "general_manager",
  "maintenance",
] as const;
export type GatePassInSection = (typeof GATE_PASS_IN_SECTIONS)[number];
export const GATE_PASS_IN_SECTION_LABELS: Record<GatePassInSection, string> = {
  arrival: "Arrival",
  processing: "Processing",
  packing: "Packing",
  dispatch: "Dispatch",
  stores: "Stores",
  sales_return: "Sales Return",
  inventory: "Inventory",
  qa_lab: "QA Lab",
  foundation_seed: "Foundation Seed",
  general_manager: "General Manager",
  maintenance: "Maintenance",
};

export const GATE_PASS_IN_TRANSIT_MODES = [
  "by_vehicle",
  "by_hand",
  "courier",
] as const;
export type GatePassInTransitMode = (typeof GATE_PASS_IN_TRANSIT_MODES)[number];
export const GATE_PASS_IN_TRANSIT_MODE_LABELS: Record<
  GatePassInTransitMode,
  string
> = {
  by_vehicle: "By Vehicle",
  by_hand: "By Hand",
  courier: "Courier",
};

export const GATE_PASS_IN_UNITS = [
  "bag",
  "box",
  "kg",
  "ltr",
  "nos",
  "metre",
  "bundle",
] as const;
export type GatePassInUnit = (typeof GATE_PASS_IN_UNITS)[number];
export const GATE_PASS_IN_UNIT_LABELS: Record<GatePassInUnit, string> = {
  bag: "Bag",
  box: "Box",
  kg: "Kg",
  ltr: "Ltr",
  nos: "Nos",
  metre: "Metres",
  bundle: "Bundle",
};

export const GATE_PASS_IN_STATUSES = [
  "partial",
  "submitted",
  "cancelled",
] as const;
export type GatePassInStatus = (typeof GATE_PASS_IN_STATUSES)[number];
export const GATE_PASS_IN_STATUS_LABELS: Record<GatePassInStatus, string> = {
  partial: "Partial",
  submitted: "Submitted",
  cancelled: "Cancelled",
};

/**
 * The arrival forms a submitted pass can be earmarked for, picked by the arrival executive on the
 * gate's Inward Done tab. Only forms a manual pass can feed: S-Capture trucks carry their own gate
 * entry, Stock Transfer – Plant is on hold, and Cob Arrival happens at a third-party dryer, not at
 * our barrier. Form codes, so the titles come from the form registry.
 */
export const GATE_PASS_IN_ARRIVAL_FORMS = [
  "arrival_pdn",
  "arrival_trading",
  "arrival_cob_receipt",
] as const;
export type GatePassInArrivalForm = (typeof GATE_PASS_IN_ARRIVAL_FORMS)[number];

/**
 * What an arrival starts with from the pass it is raised against, in the arrival's own transport keys.
 * The guard's three modes are the arrival's three: By Vehicle is Transport, Courier is Courier, By Hand
 * is Hand Delivery. The pass's LR No. is the Lorry Receipt No.; on a courier the AWB / LR No. is the
 * Docket No. Blank values are left out, so the arrival's own field stays empty rather than "".
 *
 * The pass NUMBER is not here: the server stamps that itself (`transport.gatePassIn`).
 */
export function arrivalTransportFromGatePass(pass: {
  transitMode: string;
  transport: Partial<Record<GatePassInTransportField, string>>;
  lrNo: string | null;
}): Record<string, string> {
  const t = pass.transport;
  const picked: Record<string, string | null | undefined> =
    pass.transitMode === "courier"
      ? {
          mode: "courier",
          courierName: t.courierName,
          docketNo: t.awbNumber ?? pass.lrNo,
        }
      : pass.transitMode === "by_hand"
        ? { mode: "hand_delivery", deliveryPersonName: t.personName }
        : {
            mode: "transport",
            // The slim form records the driver, not the firm (2026-09-21): the driver is the name.
            transportName: t.transportName?.trim()
              ? t.transportName
              : t.driverName,
            vehicleNumber: t.vehicleNumber,
            lorryReceiptNumber: pass.lrNo,
            driverName: t.driverName,
            driverPhone: t.driverMobile,
          };
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(picked)) if (v?.trim()) out[k] = v.trim();
  return out;
}

// ------------------------------------------------------------------ Transport, by transit mode

/** Every transport key a pass can carry. Which ones apply is the transit mode's call. */
export type GatePassInTransportField =
  | "transportName"
  | "vehicleNumber"
  | "driverName"
  | "driverMobile"
  | "personName"
  | "personMobile"
  | "courierName"
  | "awbNumber";

export const GATE_PASS_IN_TRANSPORT_LABELS: Record<
  GatePassInTransportField,
  string
> = {
  transportName: "Transporter Name",
  vehicleNumber: "Vehicle No",
  driverName: "Driver Name",
  driverMobile: "Driver Mobile",
  personName: "Person Name",
  personMobile: "Person Mobile",
  courierName: "Courier Name",
  awbNumber: "AWB / LR No",
};

/**
 * The fields each mode accepts, in screen order, and which of them are required. `onForm: false` is
 * still accepted from an older client and kept on its passes, but the form no longer asks for it.
 */
export const GATE_PASS_IN_MODE_FIELDS: Record<
  GatePassInTransitMode,
  readonly {
    key: GatePassInTransportField;
    required: boolean;
    onForm?: false;
  }[]
> = {
  by_vehicle: [
    // Plate first, as on the S-Capture inward.
    { key: "vehicleNumber", required: true },
    // Dropped from the form 2026-09-21, back 2026-10-29 (user): the transporter's name, optional; it
    // becomes the arrival's Transport Name.
    { key: "transportName", required: false },
    { key: "driverName", required: false },
    { key: "driverMobile", required: false },
  ],
  by_hand: [
    { key: "personName", required: true },
    { key: "personMobile", required: false },
  ],
  courier: [
    { key: "courierName", required: true },
    { key: "awbNumber", required: false },
  ],
};

// \p{M} keeps Devanagari names whole: their vowel signs are marks, not letters.
const PERSON_OR_FIRM = /^[\p{L}\p{M}\d .&()'/,-]+$/u;
const PERSON_OR_FIRM_MESSAGE =
  "Use letters, digits, spaces and . & ( ) ' / , - only";

const TRANSPORT_RULES: Record<
  GatePassInTransportField,
  {
    max: number;
    pattern?: RegExp;
    message?: string;
    normalize?: (v: string) => string;
  }
> = {
  // Plate FORMAT is a warning the API returns, never a refusal at the barrier.
  vehicleNumber: { max: 30 },
  transportName: {
    max: 150,
    pattern: PERSON_OR_FIRM,
    message: PERSON_OR_FIRM_MESSAGE,
  },
  driverName: {
    max: 150,
    pattern: PERSON_OR_FIRM,
    message: PERSON_OR_FIRM_MESSAGE,
  },
  personName: {
    max: 150,
    pattern: PERSON_OR_FIRM,
    message: PERSON_OR_FIRM_MESSAGE,
  },
  courierName: {
    max: 150,
    pattern: PERSON_OR_FIRM,
    message: PERSON_OR_FIRM_MESSAGE,
  },
  driverMobile: {
    max: 20,
    pattern: MOBILE_PATTERN,
    message: MOBILE_MESSAGE,
    normalize: normalizeMobile,
  },
  personMobile: {
    max: 20,
    pattern: MOBILE_PATTERN,
    message: MOBILE_MESSAGE,
    normalize: normalizeMobile,
  },
  awbNumber: {
    max: 100,
    pattern: /^[A-Za-z0-9/-]+$/,
    message: "Use letters, digits, / and - only",
  },
};

export function normalizeGatePassInField(
  key: GatePassInTransportField,
  value: string | null | undefined
): string {
  const trimmed = (value ?? "").trim();
  return TRANSPORT_RULES[key].normalize?.(trimmed) ?? trimmed;
}

/** Why a transport value is refused, or undefined. The screen and the API both ask this. */
export function gatePassInFieldError(
  key: GatePassInTransportField,
  value: string | null | undefined,
  { required = false }: { required?: boolean } = {}
): string | undefined {
  const rule = TRANSPORT_RULES[key];
  const v = normalizeGatePassInField(key, value);
  if (!v) return required ? "Required" : undefined;
  if (v.length > rule.max) return `Keep it to ${rule.max} characters`;
  if (rule.pattern && !rule.pattern.test(v)) return rule.message;
  return undefined;
}

// ------------------------------------------------------------------ Parties

/** States and union territories, as the "Add New Company" State list offers them. */
export const INDIAN_STATES = [
  "Andaman and Nicobar Islands",
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chandigarh",
  "Chhattisgarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jammu and Kashmir",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Ladakh",
  "Lakshadweep",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Puducherry",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
] as const;
export type IndianState = (typeof INDIAN_STATES)[number];

export const PINCODE_PATTERN = /^[1-9]\d{5}$/;
export const PINCODE_MESSAGE = "Enter a 6-digit PIN code";

// ------------------------------------------------------------------ Manual pass numbers

/**
 * How a pass got its number: minted from the plant's series, or typed off the paper pass security
 * wrote where the load was unloaded (a hired warehouse, field or cold store). Unique per plant.
 */
export const GATE_PASS_NUMBER_MODES = ["system", "manual"] as const;
export type GatePassNumberMode = (typeof GATE_PASS_NUMBER_MODES)[number];

export const MANUAL_PASS_NO_MAX = 30;
export const MANUAL_PASS_NO_PATTERN = /^[A-Za-z0-9][A-Za-z0-9/\- ]*$/;
export const MANUAL_PASS_NO_MESSAGE = "Letters, digits, / and - only";
export const DEFAULT_UNLOADING_COUNTRY = "India";

/**
 * True when a typed number is in this plant's own system shape (`D/Y/UL/015`). Refused: the series
 * would later mint the same number and that save would hit the per-plant unique index.
 */
export function isSystemGatePassInFormat(
  value: string,
  plantCode: string
): boolean {
  const n = value.replace(/\s+/g, "").toUpperCase();
  const plant = plantCode.trim().toUpperCase();
  if (!plant || !n.startsWith(`${plant}/`)) return false;
  return /^[^/]+\/(?:[^/]+\/)?UL\/\d+$/.test(n);
}

/** One party is one name in one city: the duplicate check, case- and space-insensitive. */
export const normaliseGatePartyKey = (name: string, city: string): string =>
  `${name}|${city}`.trim().toLowerCase().replace(/\s+/g, " ");
